import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { scrollToId } from "@/lib/scroll";
import { useCart } from "@/shop/CartContext";

const LINKS = [
  { label: "Rubriche", id: "rubriche", testid: "nav-link-rubriche" },
  { label: "Numeri", id: "numeri", testid: "nav-link-numeri" },
  { label: "Shop", to: "/shop", testid: "nav-link-shop" },
  { label: "Manifesto", id: "manifesto", testid: "nav-link-manifesto" },
  { label: "Diario", id: "diario", testid: "nav-link-diario" },
];

const go = (id) => scrollToId(id);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
          scrolled
            ? "bg-ink/80 backdrop-blur-md border-b border-ink-border"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0)}
            className="flex items-center gap-3 group"
          >
            <LogoMark className="h-8 w-8 md:h-9 md:w-9 transition-transform duration-500 group-hover:rotate-[8deg]" />
            <span className="font-serif text-base md:text-lg tracking-[0.3em] uppercase">
              Stillwater
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={l.testid}
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-mist hover:text-bone transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  data-testid={l.testid}
                  onClick={() => go(l.id)}
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-mist hover:text-bone transition-colors duration-300"
                >
                  {l.label}
                </button>
              ),
            )}
            <Link
              to="/cart"
              data-testid="nav-cart"
              className="relative text-mist hover:text-bone transition-colors duration-300 p-1"
              aria-label="Carrello"
            >
              <ShoppingBag size={17} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-1.5 -right-1.5 bg-ember text-ink font-mono text-[9px] leading-none rounded-full min-w-[15px] px-1 py-0.5 text-center"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              data-testid="nav-cta"
              onClick={() => go("diario")}
              className="font-mono text-[11px] uppercase tracking-[0.25em] bg-ember text-ink px-5 py-2.5 rounded-full hover:bg-ember-bright transition-colors duration-300"
            >
              Iscriviti
            </button>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            className="md:hidden text-bone p-2"
            onClick={() => setOpen(true)}
            aria-label="Apri menu"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 bg-ink flex flex-col"
          >
            <div className="flex items-center justify-between px-5 h-16">
              <LogoMark className="h-8 w-8" />
              <button
                data-testid="nav-menu-close"
                className="text-bone p-2"
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => {
                const inner = (
                  <motion.span
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="block text-left font-serif text-5xl py-3 border-b border-ink-border hover:text-ember transition-colors"
                  >
                    {l.label}
                  </motion.span>
                );
                return l.to ? (
                  <Link key={l.id} to={l.to} data-testid={`nav-mobile-${l.testid}`} onClick={() => setOpen(false)}>
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={l.id}
                    data-testid={`nav-mobile-${l.testid}`}
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => go(l.id), 60);
                    }}
                    className="text-left"
                  >
                    {inner}
                  </button>
                );
              })}
              <Link
                to="/cart"
                data-testid="nav-mobile-cart"
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ember"
              >
                <ShoppingBag size={16} strokeWidth={1.5} />
                Carrello {count > 0 && `(${count})`}
              </Link>
            </div>
            <p className="px-8 pb-10 font-mono text-[10px] uppercase tracking-[0.3em] text-faint">
              Senza sponsor — senza filtri
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
